import L from 'leaflet'
import { useTheme } from '@mui/material'
import { useRef, useMemo, useEffect, useState, useCallback } from 'react'
import { useMap, useMapEvents, Polygon, Polyline, CircleMarker } from 'react-leaflet'

const CLOSE_THRESHOLD_PIXELS = 12
const DRAG_THRESHOLD_PIXELS = 5

/**
 * Leaflet layer component for polygon drawing
 * Renders inside MapContainer and handles all drawing interactions
 * Panning is always enabled - clicks are distinguished from drags
 *
 * Performance: trailing line and cursor dot are imperative Leaflet layers
 * (no React state changes on mousemove). isNearFirstPoint is local state
 * that only triggers re-renders when the close zone is entered/exited.
 */
export const PolygonDrawLayer = ({
  points,
  isComplete,
  addPoint,
  closePolygon,
  updateCursor,
  onCursorScreenPos,
  onLastPointScreenPos,
  centroid,
  onCentroidScreenPos,
}) => {
  const { palette } = useTheme()
  const map = useMap()
  const mouseDownPos = useRef(null)
  const cursorLatLng = useRef(null)       // imperative cursor tracking, no state
  const [isNearFirstPoint, setIsNearFirstPoint] = useState(false)
  const isNearRef = useRef(false)          // sync copy for event handlers (avoids stale closure)

  // --- Stable pathOptions (never recreated unless theme changes) ---
  const polygonOptions = useMemo(() => ({
    color: palette.warning.dark,
    fillColor: palette.warning.light,
    fillOpacity: 0.3,
    weight: 2,
  }), [palette.warning.dark, palette.warning.light])

  const polylineOptions = useMemo(() => ({
    color: palette.warning.dark,
    weight: 2,
  }), [palette.warning.dark])

  const vertexOptions = useMemo(() => ({
    color: palette.warning.dark,
    fillColor: palette.warning.light,
    fillOpacity: 0.8,
    weight: 2,
  }), [palette.warning.dark, palette.warning.light])

  const firstPointOptions = useMemo(() => ({
    color: palette.info.dark,
    fillColor: palette.info.light,
    fillOpacity: 0.8,
    weight: 2,
  }), [palette.info.dark, palette.info.light])

  const closeIndicatorOptions = useMemo(() => ({
    color: palette.info.dark,
    fillColor: palette.info.light,
    fillOpacity: 1,
    weight: 3,
  }), [palette.info.dark, palette.info.light])

  // --- Imperative Leaflet layers for trailing line and cursor dot ---
  const trailingLineRef = useRef(null)
  const cursorDotRef = useRef(null)

  useEffect(() => {
    const line = L.polyline([], {
      color: palette.warning.dark,
      weight: 2,
      dashArray: '6, 6',
    }).addTo(map)
    const dot = L.circleMarker([0, 0], {
      radius: 4,
      color: palette.warning.dark,
      fillColor: palette.warning.dark,
      fillOpacity: 0.3,
      weight: 2,
    })
    trailingLineRef.current = line
    cursorDotRef.current = dot
    return () => {
      line.remove()
      dot.remove()
    }
  }, [map]) // eslint-disable-line react-hooks/exhaustive-deps -- runs once on mount

  // Sync imperative layer styles if palette changes
  useEffect(() => {
    trailingLineRef.current?.setStyle({ color: palette.warning.dark })
    cursorDotRef.current?.setStyle({ color: palette.warning.dark, fillColor: palette.warning.dark })
  }, [palette.warning.dark])

  // Imperatively update trailing line and cursor dot positions
  const syncTrailingLine = useCallback((cursor, pts, nearFirst) => {
    const line = trailingLineRef.current
    const dot = cursorDotRef.current
    if (!line || !dot) return

    if (!cursor || pts.length === 0) {
      line.setLatLngs([])
      if (map.hasLayer(dot)) dot.remove()
      return
    }

    const lastPt = pts[pts.length - 1]
    const target = nearFirst ? pts[0] : { y: cursor.lat, x: cursor.lng }
    line.setLatLngs([[lastPt.y, lastPt.x], [target.y, target.x]])

    if (nearFirst) {
      if (map.hasLayer(dot)) dot.remove()
    } else {
      dot.setLatLng([cursor.lat, cursor.lng])
      if (!map.hasLayer(dot)) dot.addTo(map)
    }
  }, [map])

  // When points change (new point added) or drawing completes, re-sync trailing line
  useEffect(() => {
    if (isComplete) {
      trailingLineRef.current?.setLatLngs([])
      if (map.hasLayer(cursorDotRef.current)) cursorDotRef.current.remove()
      return
    }
    syncTrailingLine(cursorLatLng.current, points, isNearRef.current)
  }, [points, isComplete, syncTrailingLine, map])

  // Convert centroid to screen pixels whenever it changes
  useEffect(() => {
    if (!centroid || !onCentroidScreenPos) return
    try {
      const containerPoint = map.latLngToContainerPoint([centroid.y, centroid.x])
      onCentroidScreenPos({ x: containerPoint.x, y: containerPoint.y })
    } catch {
      onCentroidScreenPos(null)
    }
  }, [centroid, map, onCentroidScreenPos])

  // Convert last point to screen pixels whenever points change
  useEffect(() => {
    if (!onLastPointScreenPos) return
    if (points.length === 0) { onLastPointScreenPos(null); return }
    try {
      const lastPoint = points[points.length - 1]
      const containerPoint = map.latLngToContainerPoint([lastPoint.y, lastPoint.x])
      onLastPointScreenPos({ x: containerPoint.x, y: containerPoint.y })
    } catch {
      onLastPointScreenPos(null)
    }
  }, [points, map, onLastPointScreenPos])

  // Map event handlers
  useMapEvents({
    mousedown: (e) => {
      mouseDownPos.current = map.latLngToContainerPoint(e.latlng)
    },
    click: (e) => {
      if (isComplete) return
      if (mouseDownPos.current) {
        const clickPos = map.latLngToContainerPoint(e.latlng)
        const dx = clickPos.x - mouseDownPos.current.x
        const dy = clickPos.y - mouseDownPos.current.y
        if (Math.sqrt(dx * dx + dy * dy) > DRAG_THRESHOLD_PIXELS) {
          mouseDownPos.current = null
          return
        }
      }
      mouseDownPos.current = null
      if (points.length >= 3 && isNearRef.current) {
        closePolygon()
        return
      }
      addPoint(e.latlng)
    },
    mousemove: (e) => {
      if (isComplete) return
      cursorLatLng.current = e.latlng
      updateCursor(e.latlng)

      if (onCursorScreenPos) {
        const cp = map.latLngToContainerPoint(e.latlng)
        onCursorScreenPos({ x: cp.x, y: cp.y })
      }

      // Compute isNear directly — only setState when the boolean flips (minimises re-renders)
      let near = false
      if (points.length >= 3) {
        const firstPt = points[0]
        const cursorPx = map.latLngToContainerPoint(e.latlng)
        const firstPx = map.latLngToContainerPoint([firstPt.y, firstPt.x])
        const dx = cursorPx.x - firstPx.x
        const dy = cursorPx.y - firstPx.y
        near = Math.sqrt(dx * dx + dy * dy) < CLOSE_THRESHOLD_PIXELS
      }
      if (near !== isNearRef.current) {
        isNearRef.current = near
        setIsNearFirstPoint(near)
      }

      // Update trailing line imperatively — no React render triggered
      syncTrailingLine(e.latlng, points, near)
    },
    mouseout: () => {
      cursorLatLng.current = null
      updateCursor(null)
      if (onCursorScreenPos) onCursorScreenPos(null)
      trailingLineRef.current?.setLatLngs([])
      if (map.hasLayer(cursorDotRef.current)) cursorDotRef.current.remove()
      if (isNearRef.current) {
        isNearRef.current = false
        setIsNearFirstPoint(false)
      }
    },
    zoom: () => {
      if (cursorLatLng.current && onCursorScreenPos) {
        const cp = map.latLngToContainerPoint(cursorLatLng.current)
        onCursorScreenPos({ x: cp.x, y: cp.y })
      }
      if (points.length > 0 && onLastPointScreenPos) {
        const lastPoint = points[points.length - 1]
        const cp = map.latLngToContainerPoint([lastPoint.y, lastPoint.x])
        onLastPointScreenPos({ x: cp.x, y: cp.y })
      }
      // Re-sync trailing line after zoom (pixel positions changed)
      syncTrailingLine(cursorLatLng.current, points, isNearRef.current)
    },
  })

  const positions = useMemo(() => points.map(p => [p.y, p.x]), [points])

  // First vertex — re-memoizes only when close-zone state changes, not on every cursor move
  const firstVertex = useMemo(() => {
    if (points.length === 0) return null
    const showClose = !isComplete && isNearFirstPoint
    return (
      <CircleMarker
        key={0}
        center={[points[0].y, points[0].x]}
        radius={showClose ? 10 : (!isComplete ? 7 : 5)}
        pathOptions={showClose ? closeIndicatorOptions : (!isComplete ? firstPointOptions : vertexOptions)}
      />
    )
  }, [points, isComplete, isNearFirstPoint, closeIndicatorOptions, firstPointOptions, vertexOptions])

  // Remaining vertices — only re-memoize when points/completion changes, never on cursor move
  const restVertices = useMemo(() => {
    const slice = isComplete ? points.slice(1) : points.slice(1)
    return slice.map((point, idx) => (
      <CircleMarker
        key={idx + 1}
        center={[point.y, point.x]}
        radius={5}
        pathOptions={vertexOptions}
      />
    ))
  }, [points, isComplete, vertexOptions])

  return (
    <>
      {/* Completed polygon fill */}
      {isComplete && positions.length >= 3 && (
        <Polygon positions={positions} pathOptions={polygonOptions} />
      )}

      {/* Polygon outline (while drawing) */}
      {!isComplete && positions.length >= 1 && (
        <Polyline positions={positions} pathOptions={polylineOptions} />
      )}

      {/* Trailing line and cursor dot are imperative Leaflet layers — not rendered here */}

      {/* Vertex markers */}
      {firstVertex}
      {restVertices}
    </>
  )
}
